import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Inject,
  NgZone,
  OnDestroy,
  Output,
  PLATFORM_ID,
  ViewChild,
} from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

let registered = false;

@Component({
  selector: 'app-contact',
  standalone: true,
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './contact.component.html',
  styleUrls: ['./contact.component.scss'],
})
export class ContactComponent implements AfterViewInit, OnDestroy {
  /** The page owns the consultation dialog. */
  @Output() consult = new EventEmitter<void>();

  @ViewChild('root') root?: ElementRef<HTMLElement>;
  @ViewChild('head') head?: ElementRef<HTMLElement>;
  @ViewChild('grid') grid?: ElementRef<HTMLElement>;

  reduced = false;

  private ctx?: gsap.Context;
  private io?: IntersectionObserver;
  private cleanups: Array<() => void> = [];
  private isBrowser: boolean;

  constructor(
    private zone: NgZone,
    private host: ElementRef<HTMLElement>,
    @Inject(PLATFORM_ID) platformId: Object
  ) {
    this.isBrowser = isPlatformBrowser(platformId);
  }

  onConsult(): void {
    this.consult.emit();
  }

  ngAfterViewInit(): void {
    if (!this.isBrowser) return;

    this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (this.reduced) return;

    if (!registered) {
      gsap.registerPlugin(ScrollTrigger);
      registered = true;
    }

    this.zone.runOutsideAngular(() => {
      this.ctx = gsap.context(() => this.buildEntrance(), this.host.nativeElement);
      this.watchVisibility();
    });
  }

  private buildEntrance(): void {
    const tl = gsap.timeline({
      defaults: { ease: 'power4.out' },
      scrollTrigger: {
        trigger: this.head!.nativeElement,
        start: 'top 84%',
        once: true,
      },
    });

    // fromTo, never from — see the note in leaders.component.ts. A ScrollTrigger
    // refresh invalidates from() tweens, which then record their own from-state
    // as the destination and animate opacity 0 -> 0, leaving the section blank.
    const done = { clearProps: 'opacity,transform' };

    tl.fromTo('.ct__badge', { opacity: 0, y: 20 }, { opacity: 1, y: 0, duration: 0.7, ...done })
      .fromTo('.ct__title', { opacity: 0, y: 28 }, { opacity: 1, y: 0, duration: 0.8, ...done }, '-=0.44')
      .fromTo('.ct__divider', { opacity: 0, scaleX: 0 }, { opacity: 1, scaleX: 1, duration: 0.6, ...done }, '-=0.5')
      .fromTo('.ct__lede', { opacity: 0, y: 18 }, { opacity: 1, y: 0, duration: 0.7, ...done }, '-=0.5');

    // cards rise, then their icons pop in behind them
    const cards = gsap.timeline({
      scrollTrigger: {
        trigger: this.grid!.nativeElement,
        start: 'top 88%',
        once: true,
      },
    });

    cards
      .fromTo(
        '.ct__card',
        { opacity: 0, y: 50 },
        { opacity: 1, y: 0, duration: 0.8, ease: 'power4.out', stagger: 0.08, ...done }
      )
      .fromTo(
        '.ct__ico',
        { opacity: 0, scale: 0.6, rotate: -12 },
        {
          opacity: 1,
          scale: 1,
          rotate: 0,
          duration: 0.6,
          ease: 'back.out(1.7)',
          stagger: 0.08,
          ...done,
        },
        '-=0.55'
      );

    gsap.fromTo(
      '.ct__cta',
      { opacity: 0, y: 30 },
      {
        opacity: 1,
        y: 0,
        duration: 0.8,
        ease: 'power4.out',
        ...done,
        scrollTrigger: {
          trigger: '.ct__cta',
          start: 'top 94%',
          once: true,
        },
      }
    );
  }

  private watchVisibility(): void {
    const el = this.root?.nativeElement;
    if (!el || typeof IntersectionObserver === 'undefined') return;

    this.io = new IntersectionObserver(
      (entries) =>
        el.classList.toggle('is-paused', !entries.some((e) => e.isIntersecting)),
      { threshold: 0 }
    );

    this.io.observe(el);
    this.cleanups.push(() => this.io?.disconnect());
  }

  ngOnDestroy(): void {
    this.cleanups.forEach((fn) => fn());
    this.cleanups = [];
    this.io?.disconnect();
    this.ctx?.revert();
  }
}
